import { useState, ReactElement } from "react";
import { useParams } from "react-router-dom";
import { useNotesStore } from "~/stores/useNotesStore";
import { useMusicStore } from "~/stores/useMusicStore";
import { useAuthStore } from "~/stores/useAuthStore";
import PianoKeyboard from "~/components/PianoKeyboard";
import SectionHeader from "~/components/ui/SectionHeader";
import RangeSlider from "~/components/ui/RangeSlider";
import AuthModal from "~/components/AuthModal";
import ScrambleToggle from "~/components/ui/ScrambleToggle";
import ToggleGroup from "~/components/ui/ToggleGroup";
import musicData from "~/data/musicData";
import musicTiming from "~/data/musicTiming";
import { PATTERNS } from "~/types/pattern";
import type { Scale, NoteLength } from "~/types";
import type { Pattern } from "~/types/pattern";

export default function Notes(): ReactElement {
  const { deviceIndex: deviceIndexParam } = useParams<{ deviceIndex: string }>();
  const deviceIndex = parseInt(deviceIndexParam || "0", 10);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Get global timing from useNotesStore
  const noteLength = useNotesStore((state) => state.noteLength);
  const setNoteLength = useNotesStore((state) => state.setNoteLength);
  const scrambleNoteLength = useNotesStore((state) => state.scrambleNoteLength);
  const toggleScrambleNoteLength = useNotesStore(
    (state) => state.toggleScrambleNoteLength
  );

  // Get device pattern from useMusicStore
  const device = useMusicStore((state) => state.devices[deviceIndex]);
  const setDevicePattern = useMusicStore((state) => state.setDevicePattern);

  const handleUnauthenticatedClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  };

  // Show loading state until device pattern is loaded
  if (!device?.pattern) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gray-950">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  const devicePattern = device.pattern;

  // Check if this is a MIDI hardware device (not a virtual synth)
  // Virtual devices have synthType, MIDI hardware devices have productId
  const isMidiDevice = !!device.productId;

  // Determine if auth restriction should apply
  // Only MIDI hardware devices require authentication
  const requiresAuth = isMidiDevice && !isAuthenticated;

  return (
    <div className="flex-1 h-full flex flex-col">
      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      <div
        className="flex-1 overflow-auto p-4"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      >
        {/* Max-width container for large viewports */}
        <div className="max-w-5xl">
          <div className="relative">
            {/* Click Interception Overlay - only for MIDI hardware devices when not authenticated */}
            {requiresAuth && (
              <div
                className="absolute inset-0 z-10 cursor-pointer"
                onClick={handleUnauthenticatedClick}
                title="Sign in to adjust parameters"
              />
            )}

            <div
              className={`space-y-2 ${
                requiresAuth ? "pointer-events-none" : ""
              }`}
            >
              <div className="flex flex-wrap gap-2">
                {/* Structure */}
                <SectionHeader title="Structure" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleStructure}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleStructure =
                          !pattern.scrambleToggles.scrambleStructure;
                      })
                    }
                  />
                </SectionHeader>
                <ToggleGroup
                  type="single"
                  value={devicePattern.pattern.name}
                  onValueChange={(value) => {
                    const selectedPattern = PATTERNS.find((p) => p.name === value);
                    if (selectedPattern) {
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.pattern = selectedPattern;
                      });
                    }
                  }}
                  className="w-full grid grid-cols-2 gap-2"
                >
                  {PATTERNS.map((p: Pattern) => (
                    <ToggleGroup.Item
                      key={p.name}
                      value={p.name}
                      className="h-16 flex-col font-mono text-lg"
                    >
                      <span className="text-xl">{p.name}</span>
                    </ToggleGroup.Item>
                  ))}
                </ToggleGroup>

                {/* Root Note */}
                <SectionHeader title="Root Note" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleRootNote}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleRootNote =
                          !pattern.scrambleToggles.scrambleRootNote;
                      })
                    }
                  />
                </SectionHeader>

                <PianoKeyboard
                  rootNotes={musicData.rootNotes}
                  selectedNote={devicePattern.rootNote.note}
                  onNoteSelect={(note) =>
                    setDevicePattern(deviceIndex, (pattern) => {
                      pattern.rootNote = note;
                    })
                  }
                />

                {/* Scale Mode */}
                <SectionHeader title="Mode" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleMode}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleMode =
                          !pattern.scrambleToggles.scrambleMode;
                      })
                    }
                  />
                </SectionHeader>
                <ToggleGroup
                  type="single"
                  value={devicePattern.scaleMode}
                  onValueChange={(value) =>
                    setDevicePattern(deviceIndex, (pattern) => {
                      pattern.scaleMode = value as "Major" | "Minor";
                    })
                  }
                  className="w-full grid grid-cols-2 gap-2"
                >
                  <ToggleGroup.Item
                    value="Major"
                    className="h-16 flex-col font-mono text-lg"
                  >
                    <span className="text-xs text-gray-400">Bright</span>
                    <span className="text-xl">Major</span>
                  </ToggleGroup.Item>
                  <ToggleGroup.Item
                    value="Minor"
                    className="h-16 flex-col font-mono text-lg"
                  >
                    <span className="text-xs text-gray-400">Dark</span>
                    <span className="text-xl">Minor</span>
                  </ToggleGroup.Item>
                </ToggleGroup>

                {/* Scale */}
                <SectionHeader title="Scale" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleScale}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleScale =
                          !pattern.scrambleToggles.scrambleScale;
                      })
                    }
                  />
                </SectionHeader>
                <ToggleGroup
                  type="single"
                  value={devicePattern.scale.id}
                  onValueChange={(value) => {
                    const selectedScale = musicData.scales.find(
                      (s) => s.id === value
                    );
                    if (selectedScale) {
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scale = selectedScale;
                        pattern.scaleMode = selectedScale.mode;
                      });
                    }
                  }}
                  className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2"
                >
                  {musicData.scales
                    .filter((s: Scale) => s.mode === devicePattern.scaleMode)
                    .map((s: Scale) => (
                      <ToggleGroup.Item
                        key={s.id}
                        value={s.id}
                        className="h-16 flex-col font-mono text-lg"
                      >
                        <span className="text-xs text-gray-400">
                          {s.sublabel}
                        </span>
                        <span className="text-xl">{s.label}</span>
                      </ToggleGroup.Item>
                    ))}
                </ToggleGroup>

                {/* Note Length */}
                <SectionHeader title="Note Length" variant="control">
                  <ScrambleToggle
                    enabled={scrambleNoteLength}
                    onToggle={toggleScrambleNoteLength}
                  />
                </SectionHeader>
                <ToggleGroup
                  type="single"
                  value={noteLength.length.toString()}
                  onValueChange={(value) => {
                    const selected = musicTiming.noteLengths.find(
                      (n) => n.length.toString() === value
                    );
                    if (selected) {
                      setNoteLength(selected);
                    }
                  }}
                  className="w-full grid grid-cols-2 gap-2"
                >
                  {musicTiming.noteLengths.map((n: NoteLength) => (
                    <ToggleGroup.Item
                      key={n.length}
                      value={n.length.toString()}
                      className="h-16 flex-col font-mono text-lg"
                    >
                      <span className="text-xs text-gray-400">{n.name}</span>
                      <span className="text-xl">{n.label}</span>
                    </ToggleGroup.Item>
                  ))}
                </ToggleGroup>

                {/* Range */}
                <SectionHeader title="Range" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleRange}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleRange =
                          !pattern.scrambleToggles.scrambleRange;
                      })
                    }
                  />
                </SectionHeader>
                <div className="w-full space-y-4">
                  <RangeSlider
                    value={devicePattern.range ?? 24}
                    valueMin={devicePattern.rangeMin ?? 1}
                    valueMax={devicePattern.rangeMax ?? 88}
                    min={1}
                    max={88}
                    unit=" semitones"
                    onChange={(value, valueMin, valueMax) =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.range = value;
                        pattern.rangeMin = valueMin;
                        pattern.rangeMax = valueMax;
                      })
                    }
                  />
                </div>

                {/* Spread */}
                <SectionHeader title="Spread" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleSpread}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleSpread =
                          !pattern.scrambleToggles.scrambleSpread;
                      })
                    }
                  />
                </SectionHeader>
                <div className="w-full space-y-4">
                  <RangeSlider
                    value={devicePattern.spread ?? 12}
                    valueMin={devicePattern.spreadMin ?? 0}
                    valueMax={devicePattern.spreadMax ?? 88}
                    min={0}
                    max={88}
                    unit=" semitones"
                    onChange={(value, valueMin, valueMax) =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.spread = value;
                        pattern.spreadMin = valueMin;
                        pattern.spreadMax = valueMax;
                      })
                    }
                  />
                </div>

                {/* Space */}
                <SectionHeader title="Space" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleSpace}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleSpace =
                          !pattern.scrambleToggles.scrambleSpace;
                      })
                    }
                  />
                </SectionHeader>
                <div className="w-full space-y-4">
                  <RangeSlider
                    value={devicePattern.space ?? 0}
                    valueMin={devicePattern.spaceMin ?? 0}
                    valueMax={devicePattern.spaceMax ?? 100}
                    min={0}
                    max={100}
                    step={1}
                    unit=""
                    logarithmic={true}
                    onChange={(value, valueMin, valueMax) =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.space = value;
                        pattern.spaceMin = valueMin;
                        pattern.spaceMax = valueMax;
                      })
                    }
                  />
                </div>

                {/* Dissonance */}
                <SectionHeader title="Dissonance" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleDissonance}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleDissonance =
                          !pattern.scrambleToggles.scrambleDissonance;
                      })
                    }
                  />
                </SectionHeader>
                <div className="w-full space-y-4">
                  <RangeSlider
                    value={devicePattern.dissonance ?? 0}
                    valueMin={devicePattern.dissonanceMin ?? 0}
                    valueMax={devicePattern.dissonanceMax ?? 100}
                    min={0}
                    max={100}
                    step={1}
                    unit=""
                    logarithmic={true}
                    onChange={(value, valueMin, valueMax) =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.dissonance = value;
                        pattern.dissonanceMin = valueMin;
                        pattern.dissonanceMax = valueMax;
                      })
                    }
                  />
                </div>

                {/* Tonic Gravity */}
                <SectionHeader title="Tonic Gravity" variant="control">
                  <ScrambleToggle
                    enabled={devicePattern.scrambleToggles.scrambleTonicGravity}
                    onToggle={() =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.scrambleToggles.scrambleTonicGravity =
                          !pattern.scrambleToggles.scrambleTonicGravity;
                      })
                    }
                  />
                </SectionHeader>
                <div className="w-full space-y-4">
                  <RangeSlider
                    value={devicePattern.tonicGravity ?? 0}
                    valueMin={devicePattern.tonicGravityMin ?? -12}
                    valueMax={devicePattern.tonicGravityMax ?? 12}
                    min={-12}
                    max={12}
                    step={1}
                    unit=""
                    onChange={(value, valueMin, valueMax) =>
                      setDevicePattern(deviceIndex, (pattern) => {
                        pattern.tonicGravity = value;
                        pattern.tonicGravityMin = valueMin;
                        pattern.tonicGravityMax = valueMax;
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
